import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { apiCallStatus } from 'helpers/constants';
import { TimeSelectorRow } from 'sharedComponents/TimeSelectorRow';
import { GeneralTable } from 'sharedComponents/GeneralTable';
import { TableHeaderRows } from 'sharedComponents/TableHeaderRows';
import { getColumns, TIMESTAMP_TABLE_COLUMN_KEYS, SORT_DIRECTION, TEMPERATURE_UNIT_GROUP_ID, MAX_PRECISION } from './constants';
import { baseToCustomUOM, getMeasurementUnits } from 'helpers/unitHelper';
import { setMaxPrecision } from 'helpers/formatters';
import { formatDateTime } from 'helpers/dateTimeFormatter';

const FFT2DHistoricalTimestampDialog = (props) => {
    const {
        showHistoricalDialog,
        setShowHistoricalDialog,
        fftTimestampList,
        dateSelection,
        setSelectedDates,
        getFFTTimestampList,
        getFFTDataByTimestamp,
        fftDataLoadingStatus,
        selectedTimestamp,
        setSelectedTimestamp,
        selectedOrganization,
        selectedAsset,
        trendKPIsList,
        maxPrecision
    } = props;
    const { t: translate } = useTranslation();

    const headerScrollRef = useRef(null);
    const [initialSelectedTimestamp, setInitialSelectedTimestamp] = useState(selectedTimestamp);

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: null
    });
    const [filters, setFilters] = useState({
        [TIMESTAMP_TABLE_COLUMN_KEYS.TIMESTAMP]: '',
        [TIMESTAMP_TABLE_COLUMN_KEYS.SPEED]: '',
        [TIMESTAMP_TABLE_COLUMN_KEYS.LINE_FREQUENCY]: '',
        [TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE]: ''
    });

    const resolvedMaxPrecision = Number.isFinite(Number(maxPrecision)) ? Number(maxPrecision) : MAX_PRECISION;

    const handleSort = (columnKey) => {
        let direction = SORT_DIRECTION.ASC;
        if (sortConfig?.key === columnKey && sortConfig?.direction === SORT_DIRECTION.ASC) {
            direction = SORT_DIRECTION.DESC;
        }
        setSortConfig({ key: columnKey, direction });
    };
    const handleFilterChange = useCallback((columnKey, value) => {
        setFilters(prev => ({
            ...prev,
            [columnKey]: value
        }));
    }, []);

    const headerColumns = useMemo(() => [
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.RADIO, title: '', className: 'radio-cell', sortable: false, filterable: false },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.TIMESTAMP, title: translate('ABB.Powertrain.Frontend.timestampLabel')?.toUpperCase(), className: 'timestamp-cell', sortable: true, filterable: true },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.SPEED, title: translate('ABB.Powertrain.Frontend.dyn_lbl_Speed')?.toUpperCase(), className: 'speed-cell', sortable: true, filterable: true, tooltip: translate('ABB.Powertrain.Frontend.fftSpeedEstimationInfoTooltip') },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.LINE_FREQUENCY, title: translate('ABB.Powertrain.Frontend.dyn_lbl_Nameplate_LineFrequency')?.toUpperCase(), className: 'linefrequency-cell', sortable: true, filterable: true },
        { key: TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE, title: translate('ABB.Powertrain.Frontend.dyn_lbl_Temperature')?.toUpperCase(), className: 'temperature-cell', sortable: true, filterable: true }
    ], [translate]);

    const columnDefinitions = useMemo(() => {
        return getColumns(initialSelectedTimestamp, setInitialSelectedTimestamp, trendKPIsList, resolvedMaxPrecision);
    }, [initialSelectedTimestamp, trendKPIsList, resolvedMaxPrecision]);

    const getConvertedSpeed = (item) => {
        if (item?.speed === null || item?.speed === undefined) {
            return null;
        }
        return setMaxPrecision(Number(item.speed), resolvedMaxPrecision);
    };

    const getConvertedLineFrequency = (item) => {
        if (item?.lineFrequency === null || item?.lineFrequency === undefined) {
            return null;
        }
        return setMaxPrecision(Number(item.lineFrequency), resolvedMaxPrecision);
    };

    const getConvertedTemperature = (item) => {
        if (item?.skinTemperature === null || item?.skinTemperature === undefined) {
            return null;
        }
        const convertedValue = baseToCustomUOM.convertWithGroups(TEMPERATURE_UNIT_GROUP_ID, item?.skinTemperature, getMeasurementUnits());
        return setMaxPrecision(Number(convertedValue), resolvedMaxPrecision);
    };

    useEffect(() => {
        if (showHistoricalDialog) {
            getFFTTimestampList(dateSelection?.from, dateSelection?.to);
        }
    }, []);

    useEffect(() => {
        if (!showHistoricalDialog) { return; }

        // Function to attach scroll listener with retry mechanism
        const attachScrollListener = () => {
            const tableContainer = document.querySelector('.historical-timestamp-table .rc-table-content');
            const headerContainer = headerScrollRef?.current;

            if (!tableContainer || !headerContainer) {
                // Retry after a short delay if elements are not found
                const retryTimer = setTimeout(attachScrollListener, 50);
                return () => clearTimeout(retryTimer);
            }

            const handleTableScroll = () => {
                headerContainer.scrollLeft = tableContainer.scrollLeft;
            };

            tableContainer.addEventListener('scroll', handleTableScroll);

            return () => {
                tableContainer.removeEventListener('scroll', handleTableScroll);
            };
        };

        // Start attaching listener after a small initial delay
        const timer = setTimeout(attachScrollListener, 100);

        return () => clearTimeout(timer);
    }, [showHistoricalDialog, fftTimestampList?.data]);

    const filterByField = (data, filterValue, valueGetter) => {
        if (!filterValue || filterValue.trim() === '') {
            return data;
        }
        const searchTerm = filterValue.toLowerCase().trim();
        return data?.filter(item => {
            const value = valueGetter(item);
            if (value === null || value === undefined) {
                return false;
            }
            const stringValue = value.toString().toLowerCase().trim();
            return stringValue === searchTerm || stringValue.includes(searchTerm);
        });
    };

    const compareValues = (aValue, bValue, direction) => {
        const aMissing = aValue === null || aValue === undefined;
        const bMissing = bValue === null || bValue === undefined;

        if (aMissing && bMissing) {
            return 0;
        }
        if (aMissing) {
            return direction === SORT_DIRECTION.ASC ? -1 : 1;
        }
        if (bMissing) {
            return direction === SORT_DIRECTION.ASC ? 1 : -1;
        }

        if (aValue > bValue) {
            return direction === SORT_DIRECTION.ASC ? 1 : -1;
        }
        if (aValue < bValue) {
            return direction === SORT_DIRECTION.ASC ? -1 : 1;
        }
        return 0;
    };

    const filteredData = useMemo(() => {
        let data = (fftTimestampList?.data || []).slice();

        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.TIMESTAMP], (item) =>
            formatDateTime(item?.timestamp)
        );

        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.SPEED], getConvertedSpeed);

        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.LINE_FREQUENCY], getConvertedLineFrequency);

        data = filterByField(data, filters?.[TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE], getConvertedTemperature);

        // Apply sorting
        if (sortConfig.key) {
            data.sort((a, b) => {
                let aValue, bValue;

                switch (sortConfig.key) {
                    case TIMESTAMP_TABLE_COLUMN_KEYS.TIMESTAMP:
                        aValue = new Date(a.timestamp).getTime();
                        bValue = new Date(b.timestamp).getTime();
                        break;
                    case TIMESTAMP_TABLE_COLUMN_KEYS.SPEED:
                        aValue = a?.speed;
                        bValue = b?.speed;
                        break;
                    case TIMESTAMP_TABLE_COLUMN_KEYS.LINE_FREQUENCY:
                        aValue = a?.lineFrequency;
                        bValue = b?.lineFrequency;
                        break;
                    case TIMESTAMP_TABLE_COLUMN_KEYS.SKIN_TEMPERATURE:
                        aValue = a?.skinTemperature;
                        bValue = b?.skinTemperature;
                        break;
                    default:
                        return 0;
                }

                return compareValues(aValue, bValue, sortConfig.direction);
            });
        } else {
            // Default sort by timestamp descending
            data = data?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        }

        return data;
    }, [fftTimestampList?.data, filters, sortConfig, resolvedMaxPrecision]);

    const handleApplyTimestamp = () => {
        setSelectedTimestamp([initialSelectedTimestamp], selectedAsset, selectedOrganization);
        getFFTDataByTimestamp([initialSelectedTimestamp], setShowHistoricalDialog, true);
    };

    const handleSelectDate = (from, to) => {
        if (from && to) {
            getFFTTimestampList(from, to);
            setSelectedDates(from, to);
        }
    };

    const isAcceptButtonDisabled = useMemo(() => {
        const isLoading = [fftDataLoadingStatus, fftTimestampList?.loadingStatus].includes(apiCallStatus.LOADING);

        return isLoading || !initialSelectedTimestamp ||
            !fftTimestampList?.data?.length || initialSelectedTimestamp === selectedTimestamp;
    }, [
        fftDataLoadingStatus,
        fftTimestampList?.loadingStatus,
        fftTimestampList?.data,
        initialSelectedTimestamp,
        selectedTimestamp
    ]);

    return <GeneralDialog
        show={showHistoricalDialog}
        size={'lg'}
        close={() => setShowHistoricalDialog(false)}
        title={translate('ABB.Powertrain.Frontend.vibrationFFTSelectTimestamp')}
        closeButton={true}
        acceptButtonProps={{
            text: translate('ABB.Powertrain.Frontend.select'),
            onClick: handleApplyTimestamp,
            disabled: isAcceptButtonDisabled,
        }}
        cancelButtonProps={{
            text: translate('ABB.Powertrain.Frontend.cancelLabel'),
            onClick: () => { setShowHistoricalDialog(false); },
            disabled: [fftDataLoadingStatus, fftTimestampList?.loadingStatus].includes(apiCallStatus.LOADING),
        }}
        modalClassName={'historical-timestamp-modal'}
    >
        <LoadingStatusWrapper useCustomAnchor transparent loadingStatus={[fftDataLoadingStatus, fftTimestampList?.loadingStatus]}>
            <div className='dialog-header-container'>
                <div className='dialog-header-row'>
                    <label>{translate('ABB.Powertrain.Frontend.date')}</label>
                </div>
                <div className='dialog-header-row'>
                    <TimeSelectorRow
                        fromDate={dateSelection?.from}
                        toDate={dateSelection?.to}
                        label=''
                        handleSelectDate={handleSelectDate}
                        maxDaysRange={365}
                        maxDaysBack={365}
                        preSelectedDays={7}
                        numberOfMonths={2}
                        footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax365DaysNote')}
                    />
                </div>
            </div>
            <TableHeaderRows
                ref={headerScrollRef}
                columns={headerColumns}
                sortConfig={sortConfig}
                onSort={handleSort}
                filters={filters}
                onFilterChange={handleFilterChange}
                translate={translate}
            />
            <GeneralTable
                className='historical-timestamp-table'
                tableKey='timestamp'
                columns={columnDefinitions}
                data={filteredData}
                emptyText={translate('ABB.Powertrain.Frontend.noDataLabel')}
                type='primary'
                rowClickEvent={(record) => setInitialSelectedTimestamp(record?.timestamp)}
                scroll={{ x: 900 }}
            />
        </LoadingStatusWrapper>
    </GeneralDialog>;
};

FFT2DHistoricalTimestampDialog.propTypes = {
    showHistoricalDialog: PropTypes.bool,
    setShowHistoricalDialog: PropTypes.func,
    fftTimestampList: PropTypes.object,
    dateSelection: PropTypes.object,
    setSelectedDates: PropTypes.func,
    getFFTTimestampList: PropTypes.func,
    getFFTDataByTimestamp: PropTypes.func,
    fftDataLoadingStatus: PropTypes.string,
    trendKPIsList: PropTypes.array,
    maxPrecision: PropTypes.number
};

export default FFT2DHistoricalTimestampDialog;
